import { useNavigate } from "@tanstack/react-router";
import {
	ArrowLeftRight,
	Briefcase,
	ChevronDown,
	Globe,
	Landmark,
	type LucideIcon,
	MessagesSquare,
} from "lucide-react";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHasOpsAccess } from "@/hooks/use-ops-access";
import { awayStore } from "@/lib/lti/instance-switch";
import { coreStore } from "@/lib/stores/core-store";
import { opsUiStore } from "@/lib/stores/ops-ui-store";
import { uiStore } from "@/lib/stores/ui-store";
import {
	SIDEBAR_CARD,
	SIDEBAR_CARD_BUTTON,
	SidebarCardButton,
} from "./sidebar-card-button";

export type ClientKey = "app" | "operations" | "organisation" | "instance";

/** How each client is named and drawn, wherever another client points at it. */
export const CLIENTS: Record<ClientKey, { label: string; icon: LucideIcon }> = {
	app: { label: "App", icon: MessagesSquare },
	operations: { label: "Operations", icon: Briefcase },
	organisation: { label: "Organisation", icon: Landmark },
	instance: { label: "Instance admin", icon: Globe },
};

export type ClientDestination = {
	key: ClientKey;
	label: string;
	icon: LucideIcon;
	open: () => void;
};

/**
 * The clients somebody can move to from the one they are in, never the one they
 * are in. Every client shares the one sign-in
 * (docs/adr/40-every-elaso-client-shares-one-sign-in.md), so a destination is
 * offered only to somebody it would admit, on the answer its own gate reads:
 * the operation-tool grants for the workspace on screen, then
 * `organization.me` and `instanceAdmin.me`. None while visiting a
 * workspace on another ELASO Instance, because every other client runs on this
 * one.
 *
 * Moving between the Learner Client and the Operations Client carries the
 * workspace, so each opens on the one the other was showing. The admin clients
 * have no workspace on screen, so they offer the Operations Client nowhere.
 */
export function useClientDestinations(from: ClientKey): ClientDestination[] {
	const navigate = useNavigate();
	const canOperate = useHasOpsAccess();
	const learnerClient = useLearnerClientDestination();
	const adminClients = useAdminClientDestinations();
	const away = useStore(awayStore, (s) => s.away);

	if (away) return [];

	const destinations: ClientDestination[] = [];
	if (from !== "app") {
		destinations.push(learnerClient);
	}
	if (from === "app" && canOperate) {
		destinations.push({
			key: "operations",
			...CLIENTS.operations,
			open: () => {
				opsUiStore
					.getState()
					.setCurrentWorkspaceId(uiStore.getState().currentWorkspaceId);
				navigate({ to: "/operations" });
			},
		});
	}
	return [
		...destinations,
		...adminClients.filter((client) => client.key !== from),
	];
}

/**
 * The Learner Client, from the Operations Client, opening on the workspace
 * Operations was showing. Only a workspace the Learner Client lists is carried
 * there: any other id is a workspace switch that closes its tabs and drops its
 * drafts for nothing.
 */
export function useLearnerClientDestination(): ClientDestination {
	const navigate = useNavigate();

	return {
		key: "app",
		...CLIENTS.app,
		open: () => {
			const operated = opsUiStore.getState().currentWorkspaceId;
			if (operated && coreStore.getState().workspaces.has(operated)) {
				uiStore.getState().setCurrentWorkspaceId(operated);
			}
			navigate({ to: "/app" });
		},
	};
}

/**
 * The admin clients somebody administers: the Organisation Admin Client for an
 * administrator of their own Organisation, on `organization.me`, and the ELASO
 * Instance Admin Client on `instanceAdmin.me`, where an Instance Manager owner
 * or admin opens every other Organisation. None while visiting a workspace on
 * another ELASO Instance, because both run on this one.
 */
export function useAdminClientDestinations(): ClientDestination[] {
	const navigate = useNavigate();
	const administersOrganisation = useStore(
		coreStore,
		(s) => s.administersOrganisation
	);
	const administersInstance = useStore(coreStore, (s) => s.administersInstance);
	const away = useStore(awayStore, (s) => s.away);

	if (away) return [];

	const destinations: ClientDestination[] = [];
	if (administersOrganisation) {
		destinations.push({
			key: "organisation",
			...CLIENTS.organisation,
			open: () => navigate({ to: "/organization" }),
		});
	}
	if (administersInstance) {
		destinations.push({
			key: "instance",
			...CLIENTS.instance,
			open: () => navigate({ to: "/instance" }),
		});
	}
	return destinations;
}

const SWITCH_TO = "Switch to";

/**
 * The sidebar card that lists {@link useClientDestinations}, drawn like
 * Broadcast Controls. One destination is a direct link named for where it goes;
 * several are a menu. Absent when there is nowhere to go.
 */
export function ClientSwitcher({
	from,
	collapsed = false,
}: {
	from: ClientKey;
	collapsed?: boolean;
}) {
	const destinations = useClientDestinations(from);

	const [only, ...others] = destinations;
	if (!only) return null;
	if (others.length === 0) {
		return (
			<SidebarCardButton
				collapsed={collapsed}
				icon={only.icon}
				label={only.label}
				onClick={only.open}
			/>
		);
	}

	const trigger = collapsed ? (
		<Tooltip>
			<TooltipTrigger asChild>
				<DropdownMenuTrigger asChild>
					<Button
						aria-label={SWITCH_TO}
						className="size-9"
						size="icon"
						variant="ghost"
					>
						<ArrowLeftRight className="size-4" />
					</Button>
				</DropdownMenuTrigger>
			</TooltipTrigger>
			<TooltipContent side="right">{SWITCH_TO}</TooltipContent>
		</Tooltip>
	) : (
		<div className={SIDEBAR_CARD}>
			<DropdownMenuTrigger asChild>
				<Button className={SIDEBAR_CARD_BUTTON} variant="ghost">
					<ArrowLeftRight className="size-3.5" />
					<span className="font-medium text-sm">{SWITCH_TO}</span>
					<ChevronDown className="ml-auto size-3.5" />
				</Button>
			</DropdownMenuTrigger>
		</div>
	);

	return (
		<DropdownMenu>
			{trigger}
			<DropdownMenuContent
				align="start"
				className="w-56"
				side={collapsed ? "right" : "bottom"}
			>
				{destinations.map((destination) => (
					<DropdownMenuItem key={destination.key} onSelect={destination.open}>
						<destination.icon className="size-3.5" />
						{destination.label}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
